<template>
  <!-- prettier-ignore -->
  <div
    class="product"
    :class="{'product--selected': product.selected, 'product--fresh': fresh}"
    :data-product-id="product.id"
    :data-group-id="groupId">

    <div
      class="product__recommended"
      :class="{'product__recommended--advertised': advertised}"
      v-if="recommended">
      <ovp-icon
        art="solid"
        name="star"
        v-tooltip="$t('product.recommended')"
      />
    </div>

    <div
      class="product__punchline"
      v-text="punchline ? $t(punchline) : ''"
    />

    <ovp-price
      class="product__price"
      :price="options.price"
      :prefix="prefix"
      :suffix="$t('product.currency')"
      v-tooltip="tooltip"
    />

    <div class="product__reductions">
      <template v-if="reduction.price > 0">
        <div>
          {{ $t('product.insteadOf')}}
          <ovp-price :price="reduction.price" />
        </div>

        <div class="reduction__info">
          <template v-for="(r, index) in reduction.labels">
            {{ $t(`info.${r.type}`) }} {{ Math.abs(r.amount) }} {{ $t('product.currency') }}<template v-if="reduction.labels.length - 1 !== index">;</template>
          </template>
        </div>
      </template>
    </div>

    <div
      class="product__name"
      v-text="$t(content).name"
    />

    <div
      class="product__description"
      v-html="$t(content).description"
    />

    <div class="product__actions">
      <a
        v-text="$t('product.more')"
        @click="openDetailView = true"
      />
      <template v-if="!product.selected">

        <ovp-product-action
          secondary
          tiny
          :person-id="personId"
          :product="product"
          :selected="product.selected"
          @add="add"
          @search="searchMedicalOffice(product)"
          @remove="remove"
        />
      </template>
      <template v-else>
        <div class="product__editactions">
          <button
            class="action__edit"
            @click="openDetailView = true">
            <ovp-icon
              art="solid"
              name="pencil"
            />
            <span v-text="$t('product.actions.edit')" />
          </button>

          <button
            class="action__remove"
            @click="remove">
            <ovp-icon
              art="solid"
              name="trash"
            />
            <span v-text="$t('product.actions.remove')" />
          </button>
        </div>
      </template>
    </div>

    <ovp-modal
      v-slot="{ modalProps }"
      v-model="openReplaceView"
      open>
      <ovp-product-replace
        :category-id="categoryId"
        :group-id="groupId"
        :from-product-id="fromProductId"
        :to-product-id="product.id"
        :person-id="personId"
        :source="source"
        @replace="_product => replace(_product)"
      />
    </ovp-modal>

    <ovp-modal
      v-slot="{ modalProps }"
      v-model="openDetailView"
      open>
      <div class="detail">
        <a @click="modalProps.close()">
          <ovp-icon
            art="light"
            name="chevron-left"
          />
          {{ $t('form.back') }}
        </a>

        <ovp-typography
          type="heading05"
          v-text="$t('product.moreInformation')"
        />
        <ovp-box
          class="detail__product"
          :options="categoryId !== 'KVG'"
          :category-id="categoryId"
          :group-id="groupId"
          :person-id="personId"
          :product="product"
          :source="source"
          @closed="modalProps.close()"
          @added="add"
        />
      </div>
    </ovp-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { cloneDeep } from 'lodash'

import {
  OvpBox,
  OvpPrice,
  OvpAction as OvpProductAction,
} from '@/components/Product'

import OvpProductReplace from '@/components/Product/Replace/Replace'

import OvpModal from '@/components/Modal/Modal'

import { ProductHelper } from '@/helpers'
import { BasketMixin } from '@/mixins'

import {
  EVENT_MEDICAL_OFFICE_MODAL_OPEN,
  EVENT_PRODUCT_ADDED,
  EVENT_PRODUCT_REMOVED,
  SOURCE_CONFIGURATOR,
} from '@/config/events'

export default {
  name: 'OvpProduct',

  mixins: [BasketMixin],

  components: {
    OvpBox,
    OvpModal,
    OvpPrice,
    OvpProductAction,
    OvpProductReplace,
  },

  props: {
    categoryId: {
      type: String,
      required: true,
    },

    groupId: {
      type: String,
      required: true
    },

    personId: {
      type: String,
      required: true,
    },

    product: {
      type: Object,
      required: true,
    },

    productId: {
      type: String,
      required: true,
    },

    recommended: {
      type: Boolean,
      default: false,
    },

    source: {
      type: String,
      default: SOURCE_CONFIGURATOR
    }
  },

  data () {
    return {
      advertised: true,
      fresh: false,
      fromProductId: null,

      openDetailView: false,
      openReplaceView: false,
    }
  },

  mounted () {
    // is used, to highlight the products recommendation
    window.setTimeout(() => {
      this.advertised = false
    }, 2000)

    this.$el.addEventListener('click', event => {
      if (!['A', 'BUTTON'].includes(event.target.tagName)) {
        this.openDetailView = true
      }
    })
  },

  watch: {
    'product.selected': {
      handler: function (value) {
        if (value) {
          this.fresh = true
          window.setTimeout(() => {
            this.fresh = false
          }, 1000)
        }
      }
    }
  },

  computed: {
    ...mapState(['basket']),

    content () {
      return `content.products.${this.productId}`
    },

    options () {
      return this.product.prices.find(p => p.selected) || {}
    },

    prefix () {
      return this.product?.showDiscountGroup && !this.product.selected ? this.$t('product.asof') : ''
    },

    punchline () {
      const group = `content.groups.${this.groupId}.title`
      const product = `content.products.${this.product.id}.punchline`

      if (this.$te(product)) {
        return product
      } else if (this.$te(group)) {
        return group
      } else {
        return false
      }
    },

    reduction () {
      const amount = Math.abs(this.options.reduction)
      return {
        price: amount > 0 ? this.options.price + amount : 0,
        labels: this.options?.reductions ?? [],
      }
    },

    tooltip () {
      if (this.product.messages.length > 0) {
        let content = this.product.messages
          ?.map(m => {
            return this.$t(`error.${m.key}`)
          })
          .join('; ')
          .trim()

        return {
          allowHTML: true,
          content,
        }
      }
    }
  },

  methods: {
    add (options) {
      if (this.product.upgrades.length && !options.forceAdd) {
        this.openDetailView = true
        return
      }

      if (options.replace) {
        this.openReplaceView = true
        this.fromProductId = options.productId
      } else {
        let categoryId
        let personId
        let productId
        if (options.productId) {
          categoryId = options.categoryId
          personId = options.personId
          productId = options.productId
        } else {
          categoryId = this.categoryId
          personId = this.personId
          productId = this.productId
        }

        const { source } = this
        this.$loading.start()

        ProductHelper.addProductToBasket(personId, productId)
          .then(basket => this.updateBasket(basket))
          .then(basket => {
            this.$EventBus.$emit(EVENT_PRODUCT_ADDED, {
              basket,
              categoryId,
              personId,
              productId,
              source,
            })
          })
          .finally(() => {
            if (!options.hasUpdate) this.openDetailView = false
            this.$loading.end()
          })
      }
    },

    getContent(productId, key) {
      const productPath = `content.products.${productId}`
      return this.$te(`${productPath}.${key}`) ? `${productPath}.${key}` : `product.${key}`
    },

    remove (event) {
      // stop the detail-modal of popping up!
      event.stopPropagation()

      const { categoryId, personId, productId, source } = this
      this.$loading.start()

      ProductHelper.removeProductFromBasket(personId, productId)
        .then(basket => this.updateBasket(basket))
        .then(basket => {
            this.$EventBus.$emit(EVENT_PRODUCT_REMOVED, {
              basket,
              categoryId,
              personId,
              productId,
              source: source,
            })
          })
          .finally(() => this.$loading.end())
    },

    replace (product) {
      const {
        categoryId,
        personId,
        fromProductId,
        productId,
        source,
      } = this

      const basket = cloneDeep(this.basket)
      const pBasket = basket.persons.find(p => p.personId === personId)

      pBasket.products.products[fromProductId].selected = false
      pBasket.products.products[productId] = product

      this.$loading.start()

      this.updateBasket(basket).then(basket => {

        this.$EventBus.$emit(EVENT_PRODUCT_REMOVED, {
          basket,
          categoryId,
          personId,
          productId: fromProductId,
          source: source,
        })

        this.$EventBus.$emit(EVENT_PRODUCT_ADDED, {
          basket,
          categoryId,
          personId,
          productId,
          source: source,
        })
      })
      .finally(() => {
        this.openReplaceView = false
        this.$loading.end()
      })
    },

    searchMedicalOffice (product) {
      this.$EventBus.$emit(EVENT_MEDICAL_OFFICE_MODAL_OPEN, {
        source: this.source,
        personId: this.personId,
        product,
      })
    }
  }
}
</script>

<style name="mobile" scoped>
.product {
  position: relative;
  display: grid;
  gap: 8px;
  grid-template-rows:
    minmax(0, auto)       /* title */
    32px                  /* price */
    minmax(0, auto)       /* reduction */
    minmax(25px, auto)    /* name */
    minmax(65px, auto)    /* description */
    minmax(44px, auto)    /* actions */
  ;
  padding: 15px 0 0 0;
  border: 1px solid var(--c-black);
  cursor: pointer;
  transition: all .3s ease-in-out;

  &:hover a {
    color: var(--c-sanitas-green);
  }

  &__punchline, &__price, &__reductions, &__name, &__description,  {
    padding: 0 15px;
  }

  &__recommended {
    --gradientColor: var(--c-sanitas-green);

    height: 50px;
    width: 50px;
    position: absolute;
    right: 0;
    top: 0;
    background: linear-gradient(to top right, transparent 0%, transparent 50%, var(--gradientColor) 50%, var(--gradientColor) 100%);
    font-size: 18px;
    transition: background .3s linear;

    .icon {
      margin: 5px 0 0 25px;
      color: var(--c-white);
    }

    &--advertised {
      --gradientColor: var(--c-original-green);
    }
  }

  &__punchline {
    font-weight: bold;
    font-size: 14px;
  }

  &__price {
    font-weight: bold;
    font-size: 24px;
    line-height: 32px;
  }

  &__reductions {
    font-size: 12px;
  }

  &__name {
    font-size: 20px;
  }

  &__description {
    font-size: 14px;
  }

  &__actions {
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    align-items: baseline;
    padding: 0 15px;

    transition: all .3s ease-in-out;
    
    .product__editactions {
      flex: 1 1 100%;
      padding: 8px 0 0 0;

      display: grid;
      gap: 8px;
      grid-template-columns: 1fr 55px 55px;
      grid-template-areas: 'empty edit remove';

      .action {
        &__remove { grid-area: remove; }
        &__edit   { grid-area: edit; }
      }

      button, span {
        transition: all .3s ease-in-out;
      }

      button {
        display: flex;
        flex-direction: column;
        align-items: center;
        background: none;
        border: none;
        font-size: 16px;
        cursor: pointer;

        &:hover {
          span, .icon {
            color: var(--c-sanitas-green);
          }
        }

        span {
          font-size: 10px;
          color: var(--c-black);
          text-overflow: ellipsis;
          overflow: hidden;
          height: 15px;
        }
      }
    }
  }

  &--selected {
    background-color: var(--c-original-green-lighten-4);
    border-color: var(--c-original-green-lighten-4);

    .product__actions {

      a, button .icon {
        color: var(--c-original-green);
      }
    }
  }

  &--fresh {
    background-color: var(--c-original-green-lighten-1);
  }
}

.detail {
  margin: 24px;

  .heading05 {
    margin: 24px 0 0 0;
  }

  &__product {
    margin: 24px 0 0 0;
    border: 1px solid var(--c-black);
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .product {

  }
}
</style>